/**
 * JWT token utilities for Auth0 tokens
 *
 * Provides safe token decoding and claim extraction without external dependencies.
 */

export interface TokenClaims {
  /** Subject (user ID) */
  sub: string
  /** Audience */
  aud: string | string[]
  /** Issuer */
  iss: string
  /** Expiration time (Unix timestamp) */
  exp: number
  /** Issued at (Unix timestamp) */
  iat: number
  /** Organization ID (custom claim at root level) */
  org_id?: string
  /** Organization name (custom claim at root level) */
  org_name?: string
  /** Email */
  email?: string
  /** Email verified */
  email_verified?: boolean
  [key: string]: unknown
}

/**
 * Decode a JWT token without verification
 * Note: This does NOT verify the signature. Only use for extracting claims
 * from tokens that have already been validated by Auth0.
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns Decoded token claims or null if invalid
 */
export function decodeToken(token: string): TokenClaims | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const payload = parts[1]
    if (!payload) {
      return null
    }

    // Decode base64url
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(decoded) as TokenClaims

    return claims
  } catch {
    return null
  }
}

/**
 * Extract organization ID from Auth0 token
 * Checks both namespaced and root-level claims
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns Organization ID or empty string if not found
 */
export function extractOrgId(token: string): string {
  const claims = decodeToken(token)

  // Check for org_id in multiple locations:
  // 1. Namespaced claim (https://sitecompass.au/org_id)
  // 2. Root level claim (org_id)
  // 3. Alternative namespace (https://sitecompass.com/org_id)
  const namespacedOrgId = claims?.['https://sitecompass.au/org_id'] as string | undefined
  const rootOrgId = claims?.org_id
  const altNamespaceOrgId = claims?.['https://sitecompass.com/org_id'] as string | undefined

  const orgId = namespacedOrgId || rootOrgId || altNamespaceOrgId || ''

  return orgId
}

/**
 * Extract user ID (sub claim) from Auth0 token
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns User ID or empty string if not found
 */
export function extractUserId(token: string): string {
  const claims = decodeToken(token)
  return claims?.sub || ''
}

/**
 * Check if a token is expired
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  const claims = decodeToken(token)
  if (!claims) {
    return true
  }

  const now = Math.floor(Date.now() / 1000)
  return claims.exp < now
}

/**
 * Get the expiration time of a token
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns Expiration time as Unix timestamp (seconds) or null if invalid
 */
export function getTokenExpirationTime(token: string): number | null {
  const claims = decodeToken(token)
  return claims?.exp ?? null
}

/**
 * Get the time until a token expires (in seconds)
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @returns Seconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token: string): number {
  const exp = getTokenExpirationTime(token)
  if (!exp) {
    return 0
  }

  const now = Math.floor(Date.now() / 1000)
  const timeUntilExpiration = exp - now
  return Math.max(0, timeUntilExpiration)
}

/**
 * Check if a token should be refreshed based on a buffer time
 *
 * @param token - The JWT token string (without "Bearer " prefix)
 * @param bufferSeconds - Buffer time in seconds before expiration (default: 300 = 5 minutes)
 * @returns true if token should be refreshed (within buffer time of expiration)
 */
export function shouldRefreshToken(token: string, bufferSeconds: number = 300): boolean {
  const timeUntilExpiration = getTimeUntilExpiration(token)
  return timeUntilExpiration > 0 && timeUntilExpiration <= bufferSeconds
}
